import React from 'react';
import cn from 'classnames';
import {
  noteOrReminderAction,
  Interaction as TimelineInteractionModel,
  TimelineItem,
} from '@repo/utils/models/Timeline';
import styles from './TimelineInteraction.module.scss';
import { useTheme } from '@repo/themes';
import Contact from '@repo/utils/models/Contact';
import { useWorkspaceContext } from '@repo/hooks';
import { NoteBadge, ReminderDateDisplay } from '@repo/components/headers';
import Occasion from '@repo/utils/helpers/Occasion';
import { useSelector } from 'react-redux';
import { selectNoteType } from '@repo/redux/reducers/data/notes';
import { selectNote } from '@repo/redux/actions/notes';
import sourceIconPaths from '@repo/utils/helpers/source-icon-paths';

const InteractionBadge = ({
  timelineItem,
  editable = true,
}: {
  timelineItem: TimelineItem;
  editable?: boolean;
}) => {
  const [theme] = useTheme();
  const iconKey = timelineItem.interaction?.iconKey;
  const storedNote = useSelector(selectNote(timelineItem.noteID));
  const noteType = useSelector(selectNoteType(timelineItem.noteID));

  if (!iconKey && ['note', 'reminder'].includes(timelineItem.type)) {
    return (
      <NoteBadge
        reminder={storedNote ? storedNote?.reminder : timelineItem.reminder}
        user={timelineItem.user}
        className={cn(styles.badge, 'timeline-interaction-badge')}
        noteType={noteType}
        editable={editable}
      />
    );
  }

  if (!iconKey) {
    return <span />;
  }

  return (
    <img
      className={cn(styles.badge, 'timeline-interaction-badge')}
      src={sourceIconPaths[`${iconKey}_${theme}`] || sourceIconPaths[iconKey]}
    />
  );
};

const InteractionLink = ({ interaction }: { interaction: TimelineInteractionModel }) => {
  const Component = interaction.link ? 'a' : 'span';

  const props: any = {
    href: interaction.link,
  };

  if (interaction.link?.startsWith('http')) {
    props.target = '_blank';
  }

  return !interaction.title ? null : <Component {...props}>{interaction.title.trim()}</Component>;
};

const InteractionDescription = ({
  contact,
  timelineItem,
  className,
}: {
  contact: Contact;
  timelineItem: TimelineItem;
  className?: string;
}) => {
  const { isMultiPlayer } = useWorkspaceContext();
  const contactName = contact.firstName || contact.displayName;
  const storedNote = useSelector(selectNote(String(timelineItem.noteID)));
  const noteLabel = storedNote?.type?.label && (
    <span className={styles.noteLabel}>{storedNote.type.label}</span>
  );
  const actionTaken =
    storedNote?.reminder && ['note', 'reminder'].includes(timelineItem.type)
      ? noteOrReminderAction(storedNote.reminder)
      : timelineItem.interaction.actionTaken;

  let userName = <b>{timelineItem?.user?.name}</b>;
  if (!isMultiPlayer || timelineItem?.user?.is_self) {
    userName = <>You</>;
  }

  let desc = (
    <>
      {noteLabel}
      {userName} {actionTaken} {!timelineItem.noteID ? contactName : ''}{' '}
    </>
  );

  if (!timelineItem?.user) {
    desc = (
      <>
        {noteLabel}
        {contactName} {actionTaken}{' '}
      </>
    );
  }

  let reminderDate = null;
  if (storedNote?.reminder?.date) {
    reminderDate = new Occasion(storedNote.reminder.date);
  }

  return (
    <div
      className={cn(styles.interactionDescription, className, 'timeline-interaction-description')}
    >
      {desc}
      <InteractionLink interaction={timelineItem.interaction} />
      {reminderDate && <ReminderDateDisplay date={reminderDate} />}
    </div>
  );
};

export const SidebarTimelineInteraction = ({
  contact,
  timelineItem,
  isLast,
}: {
  contact: Contact;
  timelineItem: TimelineItem;
  isLast: boolean;
}) => {
  return (
    <div className={cn(styles.container, !isLast && styles.notLast, styles.sidebar)}>
      <InteractionBadge timelineItem={timelineItem} editable={false} />
      <div className={styles.sidebarInteractionMetadata}>
        <InteractionDescription
          contact={contact}
          timelineItem={timelineItem}
          className={styles.sidebar}
        />
        <span className={cn(styles.interactionDate, styles.sidebar)}>
          {timelineItem.interaction?.sidebarDateDisplay}
        </span>
      </div>
    </div>
  );
};

export function TimelineInteraction({
  contact,
  timelineItem,
  isLast,
}: {
  contact: Contact;
  timelineItem: TimelineItem;
  isLast: boolean;
}) {
  return (
    <div className={cn(styles.container, !isLast && styles.notLast, 'timeline-container')}>
      <InteractionBadge timelineItem={timelineItem} />
      <InteractionDescription contact={contact} timelineItem={timelineItem} />
      <span className={styles.interactionDate}>{timelineItem.interaction.dateDisplay}</span>
    </div>
  );
}
